import { useCallback, useEffect, useState } from "react";

import { notFound } from "./NotFoundPage.jsx";
import { head } from "../components/common/PageHeader.jsx";
import { nodes } from "../components/common/reactHelpers.jsx";
import { money, esc } from "../utils/format.js";
import { select, btn } from "../components/forms/FormControls.jsx";
import { state } from "../state/workspaceStore.js";
import { tableViews } from "../components/tables/RecordTable.jsx";
import OrderDetailsPage from "../pages/OrderDetailsPage.jsx";

const API_BASE_URL = "https://api.praachinorganics.com";

async function apiRequest(url, options = {}) {
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => null);
    throw new Error(error?.message || `Request failed (${response.status})`);
  }

  return response;
}

// Matches your backend PaymentStatus enum.
const PAYMENT_STATUS_IDS = {
  Created: 1,
  Pending: 2,
  Success: 3,
  Failed: 4,
  Refunded: 5,
  PartiallyRefunded: 6,
};

const TERMINAL_STATUSES = ["Success", "Refunded", "PartiallyRefunded"];

function PaymentDetailsContent({ recordId }) {
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const loadPayment = useCallback(async () => {
    if (!recordId || !Number.isInteger(Number(recordId))) {
      setPayment(null);
      setLoading(false);
      setError("A valid payment ID was not provided.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiRequest(
        `/api/payments/${encodeURIComponent(recordId)}`,
      );
      const data = await response.json();

      setPayment(data);
    } catch (err) {
      setPayment(null);
      setError(err.message || "Unable to load payment details.");
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    loadPayment();
  }, [loadPayment]);

  async function handleStatusSubmit(event) {
    event.preventDefault();

    if (!payment || saving) return;

    const formData = new FormData(event.currentTarget);
    const selectedStatus = formData.get("status");
    const statusId = PAYMENT_STATUS_IDS[selectedStatus];

    if (!statusId) {
      setError("Please select a valid payment status.");
      return;
    }

    if (TERMINAL_STATUSES.includes(payment.status)) {
      setError(
        "Successful or refunded payments cannot be changed through this status endpoint.",
      );
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");

    try {
      const response = await apiRequest(
        `/api/payments/${encodeURIComponent(payment.id)}/status`,
        {
          method: "PATCH",
          body: JSON.stringify({ status: statusId }),
        },
      );

      const result = await response.json().catch(() => null);

      setMessage(result?.message || "Payment status updated successfully.");

      // Fetch the latest values from the backend.
      await loadPayment();
    } catch (err) {
      setError(err.message || "Unable to update payment status.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className={"card"}>
        <p className={"hint"}>{"Loading payment details..."}</p>
      </div>
    );
  }

  if (!payment) {
    if (error.includes("404") || error === "Payment not found.") {
      return notFound();
    }

    return (
      <div className={"card"}>
        <p className={"hint"} role={"alert"}>
          {esc(error || "Unable to find this payment.")}
        </p>
      </div>
    );
  }

  const isReadOnly = TERMINAL_STATUSES.includes(payment.status);

  const editableStatuses =
    payment.status === "Created"
      ? ["Created", "Pending", "Failed"]
      : ["Pending", "Failed"];

  return (
    <>
      {head(
        "PAYMENT DETAIL",
        payment.transactionNumber || payment.id,
        payment.paidAt || "",
      )}

      <div className={"grid-two"}>
        <section className={"card"}>
          <h2>{"Transaction"}</h2>

          {nodes(
            [
              ["Amount", money(payment.amount)],
              ["Refund recorded", "—"],
              ["Method", payment.method || "—"],
              ["Reference", payment.reference || "No external reference"],
              ["Status", payment.status],
            ].map(([label, value]) => (
              <div className={"detail-row"} key={label}>
                <span>{label}</span>
                <b>{esc(value)}</b>
              </div>
            )),
          )}

          {isReadOnly ? (
            <p className={"hint space-top"}>
              {payment.status === "Success"
                ? "This payment has succeeded. Its status cannot be changed manually."
                : "This payment is refunded or partially refunded. Use the refund workflow to manage refund status."}
            </p>
          ) : (
            <form
              id={"payment-form"}
              className={"space-top"}
              onSubmit={handleStatusSubmit}
            >
              {select(
                "Record status",
                "status",
                payment.status,
                editableStatuses,
              )}

              <p className={"hint"}>
                {
                  "Only permitted status changes can be saved. Successful payments require Razorpay verification, and refunds must use the refund workflow."
                }
              </p>

              <button className={"btn"} type={"submit"} disabled={saving}>
                {saving ? "Saving..." : "Save changes"}
              </button>
            </form>
          )}

          {error && (
            <p className={"hint"} role={"alert"}>
              {esc(error)}
            </p>
          )}

          {message && (
            <p className={"hint"} role={"status"}>
              {esc(message)}
            </p>
          )}

          <p className={"hint"}>
            {
              "Changing a status updates the local transaction record. It does not charge, capture, or refund through Razorpay."
            }
          </p>
        </section>

        <section className={"card"}>
          <h2>{"Linked order"}</h2>

          <p>
            {esc(payment.orderNumber || payment.orderId)}
            {" · "}
            {esc(payment.customerName || "Historical customer")}
          </p>

          {btn(
            "View order",
            `data-nav="order" data-id="${payment.orderId}"`,
            true,
          )}

          <div className={"divider"}></div>

          <h2>{"Gateway connection"}</h2>

          <p>
            {state.db.settings.razorpayConnected
              ? "Connected"
              : "Not connected"}
          </p>

          <p className={"hint"}>
            {
              "Razorpay account setup, KYC and gateway credentials are required for live processing."
            }
          </p>
        </section>
      </div>
    </>
  );
}

export function paymentDetail(pid) {
  return <PaymentDetailsContent recordId={pid} />;
}

export default function PaymentDetailsPage({ recordId } = {}) {
  return tableViews(paymentDetail(recordId));
}
